import React, { FC, useEffect, useState } from 'react';
import { FormGroup, Input, Label } from 'reactstrap';
import { AttendeeEmails } from '../../../../APIClient';
import { useTranslation } from '../../../../translations';
import { Fetcher, setElementInArray, useFetcher } from '../../../../utils';

interface AttendeeSelectorProps {
  readonly selectionChanged: (userIds: number[]) => void;
}

export const AttendeeSelector: FC<AttendeeSelectorProps> = ({ selectionChanged }) => {
  const [filter, setFilter] = useState<AttendeeEmails>({
    productIds: [],
    regStatus: { paid: true, unpaid: false },
    withChildren: false,
  });

  const loadedUserIds = useFetcher(async () => {
    const { userIds } = await api.getAttendeeEmails(filter);
    return userIds;
  }, [filter]);

  useEffect(() => {
    if (loadedUserIds.success) {
      selectionChanged(loadedUserIds.data);
    } else {
      selectionChanged([]);
    }
  }, [loadedUserIds.success, loadedUserIds.data]);

  return <AttendeeProductSelector filter={filter} setFilter={setFilter} />;
};

interface AttendeeProductSelectorProps {
  readonly filter: AttendeeEmails;
  readonly setFilter: (filter: AttendeeEmails) => void;
}

const AttendeeProductSelector: FC<AttendeeProductSelectorProps> = ({ filter, setFilter }) => {
  const { ts } = useTranslation();
  const products = useFetcher(async () => {
    const categories = await api.getProductCategories();
    const productsCat = categories.find(({ isRegistration }) => isRegistration);

    if (!productsCat) {
      return [];
    }

    return await api.getFullProductsByCategory(productsCat.id);
  }, []);

  if (!products.success) {
    return <Fetcher result={products} />;
  }

  return (
    <>
      <FormGroup>
        <Label for="area">{ts('products')}</Label>
        {products.data.map((product) => (
          <FormGroup check className="mt-10" key={product.id}>
            <Input
              checked={filter.productIds.includes(product.id)}
              id={`product_${product.id}`}
              onChange={(e) => {
                setFilter({
                  ...filter,
                  productIds: setElementInArray(filter.productIds, product.id, e.target.checked),
                });
              }}
              type="checkbox"
            />
            <Label check for={`product_${product.id}`}>
              {product.name}
            </Label>
          </FormGroup>
        ))}
      </FormGroup>
      <FormGroup>
        <Label for="area">{ts('registration_status')}</Label>
        <FormGroup check className="mt-10">
          <Input
            checked={filter.regStatus.paid}
            id="reg_paid"
            onChange={(e) => {
              setFilter({
                ...filter,
                regStatus: {
                  ...filter.regStatus,
                  paid: e.target.checked,
                },
              });
            }}
            type="checkbox"
          />
          <Label check for="reg_paid">
            {ts('common.paid')}
          </Label>
        </FormGroup>
        <FormGroup check className="mt-10">
          <Input
            checked={filter.regStatus.unpaid}
            id="reg_unpaid"
            onChange={(e) => {
              setFilter({
                ...filter,
                regStatus: {
                  ...filter.regStatus,
                  unpaid: e.target.checked,
                },
              });
            }}
            type="checkbox"
          />
          <Label check for="reg_unpaid">
            {ts('common.unpaid')}
          </Label>
        </FormGroup>
        <FormGroup check className="mt-10">
          <Input
            checked={filter.withChildren}
            id="reg_parents"
            onChange={(e) => {
              setFilter({
                ...filter,
                withChildren: e.target.checked,
              });
            }}
            type="checkbox"
          />
          <Label check for="reg_parents">
            {ts('with_children')}
          </Label>
        </FormGroup>
      </FormGroup>
    </>
  );
};
